/**
 * Bob Laadt Op — gedeelde JS.
 *
 * Reveal-on-scroll (stylescape-3-patroon): elementen faden in zodra ze in beeld komen.
 * Zelfde selectorlijst als het ontwerp (index.html), plus alles met [data-bob-reveal],
 * zodat nieuwe secties zich kunnen aanmelden zonder deze lijst aan te passen.
 * De CSS (.reveal / .is-visible) staat in bob-base.css en doet alleen iets zonder
 * prefers-reduced-motion.
 */

/**
 * Scroll-helper. Horizon laat vanaf 990px niet het venster maar `.page-wrapper` scrollen
 * (base.css), dus `window.scrollY` blijft daar 0 en `window`-scroll-events vuren niet.
 * Gebruik in Bob-scripts altijd:
 *   window.bobScroll.y            → huidige scrollpositie (venster óf page-wrapper)
 *   window.bobScroll.on(fn)       → fn bij elke scroll (capture, dus ook page-wrapper)
 *   window.bobScroll.off(fn)
 */
window.bobScroll = {
  get y() {
    const wrapper = document.querySelector('.page-wrapper');
    return Math.max(window.scrollY || 0, wrapper ? wrapper.scrollTop : 0);
  },
  /** @param {EventListener} fn */
  on(fn) {
    document.addEventListener('scroll', fn, { passive: true, capture: true });
  },
  /** @param {EventListener} fn */
  off(fn) {
    document.removeEventListener('scroll', fn, { capture: true });
  },
};

const REVEAL_SELECTOR = [
  '.section-head',
  '.rec-photo',
  '.rec-items > .rec-item',
  '.prod-grid > .prod-card',
  '.how-step',
  '.trust-carousel',
  '.assurances',
  '.keuzehulp-card',
  '.know-item',
  '.final-copy',
  '.final-media',
  '.newsletter',
  '.banner-copy',
  '[data-bob-reveal]',
].join(', ');

const observer =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.2, rootMargin: '0px 0px -40px 0px' }
      )
    : null;

/** @param {ParentNode} root */
function initReveal(root = document) {
  // Op een trage verbinding draait dit script pas ná de eerste paint. Wat dan al
  // in beeld staat niet meer verbergen, anders verdwijnt het en komt het pas terug
  // als er gescrold wordt.
  const painted = performance.getEntriesByName('first-contentful-paint').length > 0;
  const items = Array.from(root.querySelectorAll(REVEAL_SELECTOR))
    .filter((el) => !el.classList.contains('reveal'))
    .map((el) => ({ el, inView: painted && el.getBoundingClientRect().top < window.innerHeight }));

  items.forEach(({ el, inView }) => {
    // In de theme editor direct tonen: anders lijkt een net toegevoegde sectie leeg.
    if (!observer || inView || window.Shopify?.designMode) {
      el.classList.add('reveal', 'is-visible');
      return;
    }
    el.classList.add('reveal');
    observer.observe(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initReveal());
} else {
  initReveal();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initReveal(event.target);
});
